import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Modal, TouchableOpacity, View } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import FormInput from '../../../components/FormInput';
import { translate } from '../../../services/translate';
import TimePicker from 'react-time-picker';
import MyAppText from '../../../components/MyAppText';
import { ScrollView } from 'react-native-web-hover';
import moment from 'moment';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { makeSetStateHandler } from '../../../components/helpers/form';
import ActivitiesAndServicesLocationSelector from './ActivitiesAndServicesLocationSelector';
import PatrolIndicatorDropdown from '../../../components/app-agent/PatrolIndicatorDropdown';
import { computeEffectiveRange, formatDuration, RasItem } from './PolicePatrolScheduleDetailsHook';

interface RasEditorModalProps {
    visible: boolean;
    initialValue?: RasItem;
    rdpAddress: { cep: string; address: string; number?: string; latitude: string; longitude: string; };
    rdpRange: { start: moment.Moment; end: moment.Moment | null; } | null;
    onClose: () => void;
    onSave: (item: RasItem) => void;
}

function emptyRasDefaults(rdpAddress: RasEditorModalProps['rdpAddress']): RasItem {
    return {
        indicatorId: 0,
        actionsQty: undefined,
        publicQty: undefined,
        documentNumber: undefined,
        startTime: '',
        endTime: '',
        useParentAddress: true,
        cep: rdpAddress.cep,
        address: rdpAddress.address,
        number: rdpAddress.number,
        latitude: rdpAddress.latitude,
        longitude: rdpAddress.longitude,
    };
}

function existingRasDefaults(initialValue: RasItem, rdpAddress: RasEditorModalProps['rdpAddress']): RasItem {
    if (initialValue.useParentAddress) {
        return { ...initialValue, cep: rdpAddress.cep, address: rdpAddress.address, number: rdpAddress.number, latitude: rdpAddress.latitude, longitude: rdpAddress.longitude };
    }
    return initialValue;
}

export default function ActivitiesAndServicesDetails({ visible, initialValue, rdpAddress, rdpRange, onClose, onSave }: RasEditorModalProps) {
    if (!visible) {
        return null;
    }

    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const defaultValues = useMemo(
        () => (initialValue ? existingRasDefaults(initialValue, rdpAddress) : emptyRasDefaults(rdpAddress)),
        []
    );

    const form = useForm<RasItem>({ defaultValues, mode: 'onChange' });
    const values = useWatch({ control: form.control });

    const [pin, setPin] = useState<LatLng | undefined>(
        defaultValues.latitude && defaultValues.longitude
            ? { lat: Number(defaultValues.latitude), lng: Number(defaultValues.longitude) }
            : undefined
    );

    useEffect(() => {
        if (values.useParentAddress) {
            form.setValue('cep', rdpAddress.cep);
            form.setValue('address', rdpAddress.address);
            form.setValue('number', rdpAddress.number);
            form.setValue('latitude', rdpAddress.latitude);
            form.setValue('longitude', rdpAddress.longitude);
        }
    }, [values.useParentAddress]);

    function getRasRange(startTime?: string, endTime?: string) {
        if (!rdpRange || !startTime) {
            return null;
        }

        return computeEffectiveRange(
            startTime,
            endTime,
            undefined,
            rdpRange.start.toDate()
        );
    }

    function validateRasStart(startTime: string) {
        const range = getRasRange(startTime);

        if (!range || !rdpRange?.end || range.start.isBefore(rdpRange.end)) {
            return true;
        }

        return translate('rasStartOutsidePatrolPeriod', {
            rasStart: range.start.format('DD/MM HH:mm'),
            rdpStart: rdpRange.start.format('DD/MM HH:mm'),
            rdpEnd: rdpRange.end.format('DD/MM HH:mm'),
        });
    }

    function validateRasEnd(startTime: string, endTime: string) {
        const range = getRasRange(startTime, endTime);

        if (!range?.end || !rdpRange?.end || range.start.isSameOrAfter(rdpRange.end)) {
            return true;
        }

        if (range.end.isAfter(rdpRange.end)) {
            return translate('rasEndsAfterPatrolPeriod', {
                rasStart: range.start.format('DD/MM HH:mm'),
                rasEnd: range.end.format('DD/MM HH:mm'),
                rdpEnd: rdpRange.end.format('DD/MM HH:mm'),
            });
        }

        return true;
    }

    const rangePreview = useMemo(() => {
        if (!rdpRange || !values.startTime || !values.endTime) {
            return null;
        }

        const range = getRasRange(values.startTime, values.endTime);

        if (!range?.end || range.start.isSameOrAfter(range.end)) {
            return null;
        }

        return {
            rangeText: `${translate('activityAndServiceAcronym')}: ${range.start.format('DD/MM HH:mm')} → ${range.end.format('DD/MM HH:mm')}`,
            patrolRangeText: rdpRange.end
                ? `${translate('policePatrolScheduleAcronym')}: ${rdpRange.start.format('DD/MM HH:mm')} → ${rdpRange.end.format('DD/MM HH:mm')}`
                : `${translate('policePatrolScheduleAcronym')}: ${rdpRange.start.format('DD/MM HH:mm')}`,
            durationText: formatDuration(range.end.diff(range.start, 'minutes')),
        };
    }, [values.startTime, values.endTime, rdpRange]);

    function handleSave() {
        if (!form.formState.isValid) {
            form.trigger();
            return;
        }
        onSave(form.getValues());
    }

    const timePickerClassName = ['light', 'lightAlt'].includes(UnistylesRuntime.themeName)
        ? ['inputColor', 'inputColorLight']
        : ['inputColor', 'inputColorDark'];


    return (
        <Modal transparent visible={visible} animationType="slide" onRequestClose={onClose}>
            <View style={styles.backdrop}>
                <View style={styles.sheet(windowInfo.isMobile)}>
                    <View style={styles.sheetHandle} />
                    <MyAppText style={styles.modalHeader}>
                        {translate(initialValue ? 'editActivityAndService' : 'activityAndService')}
                    </MyAppText>

                    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ gap: 16 }}>
                        <Controller
                            control={form.control}
                            name="indicatorId"
                            rules={{ validate: (value) => value !== 0 || translate('required_field') }}
                            render={({ field }) => (
                                <PatrolIndicatorDropdown
                                    value={field.value}
                                    setValue={makeSetStateHandler(form, 'indicatorId')}
                                    zIndex={6}
                                    required
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={styles.itemView}
                                />
                            )}
                        />

                        <View style={{ flexDirection: 'row', gap: 12 }}>
                            <FormInput
                                form={form}
                                fieldName="actionsQty"
                                label={translate('actionsQuantity')}
                                mode="NUMBER"
                                keyboardType="decimal-pad"
                                labelStyle={styles.fieldsLabelStyle}
                                viewStyle={styles.itemView}
                                required
                            />
                            <FormInput
                                form={form}
                                fieldName="publicQty"
                                label={translate('publicQuantity')}
                                mode="NUMBER"
                                keyboardType="decimal-pad"
                                labelStyle={styles.smallFieldsLabelStyle}
                                viewStyle={styles.itemView}
                                required
                            />
                        </View>

                        <FormInput
                            form={form}
                            fieldName="documentNumber"
                            label={translate('document')}
                            mode="NUMBER"
                            labelStyle={styles.smallFieldsLabelStyle}
                            viewStyle={styles.itemView}
                        />

                        <View style={{ flexDirection: 'row', gap: 12 }}>
                            <Controller
                                control={form.control}
                                name="startTime"
                                rules={{
                                    required: translate('required_field'),
                                    validate: validateRasStart,
                                }}
                                render={({ field, fieldState }) => (
                                    <View style={styles.timePickerCol}>
                                        <View style={styles.timePickerLabelRow}>
                                            <MyAppText style={styles.fieldsLabelStyle}>{translate('startTime')}</MyAppText>
                                            <MyAppText style={styles.requiredStar}>(*)</MyAppText>
                                        </View>

                                        <TimePicker
                                            disableClock
                                            format="HH:mm"
                                            locale="pt-BR"
                                            maxDetail="minute"
                                            className={timePickerClassName}
                                            value={field.value}
                                            clearIcon={null}
                                            onChange={(value) => {
                                                if (value) {
                                                    field.onChange(value);
                                                    form.trigger(['startTime', 'endTime']);
                                                }
                                            }}
                                        />

                                        {fieldState.error && (
                                            <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText>
                                        )}
                                    </View>
                                )}
                            />

                            <Controller
                                control={form.control}
                                name="endTime"
                                rules={{
                                    required: translate('required_field'),
                                    validate: (endTime) => {
                                        const startTime = form.getValues('startTime');

                                        if (startTime && endTime && startTime === endTime) {
                                            return translate('startEndTimeCannotBeEqual');
                                        }

                                        if (!rdpRange || !startTime || !endTime) {
                                            return true;
                                        }

                                        return validateRasEnd(startTime, endTime);
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <View style={styles.timePickerCol}>
                                        <View style={styles.timePickerLabelRow}>
                                            <MyAppText style={styles.fieldsLabelStyle}>{translate('endTime')}</MyAppText>
                                            <MyAppText style={styles.requiredStar}>(*)</MyAppText>
                                        </View>

                                        <TimePicker
                                            disableClock
                                            format="HH:mm"
                                            locale="pt-BR"
                                            maxDetail="minute"
                                            className={timePickerClassName}
                                            value={field.value}
                                            clearIcon={null}
                                            onChange={(value) => {
                                                field.onChange(value ?? '');
                                                form.trigger(['startTime', 'endTime']);
                                            }}
                                        />

                                        {fieldState.error && (
                                            <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText>
                                        )}
                                    </View>
                                )}
                            />
                        </View>

                        {rangePreview && (
                            <View style={styles.rangePreview}>
                                <MyAppText style={styles.rangePreviewText}>{rangePreview.rangeText}</MyAppText>
                                <MyAppText style={styles.rangePreviewDuration}>{rangePreview.patrolRangeText}</MyAppText>
                                <MyAppText style={styles.rangePreviewDuration}>
                                    {translate('duration')}: {rangePreview.durationText}
                                </MyAppText>
                            </View>
                        )}

                        <TouchableOpacity
                            style={styles.checkboxRow}
                            onPress={() => form.setValue('useParentAddress', !values.useParentAddress)}
                            activeOpacity={0.7}
                        >
                            <View style={[styles.checkbox, values.useParentAddress && styles.checkboxChecked]}>
                                {values.useParentAddress && <View style={styles.checkboxMark} />}
                            </View>
                            <MyAppText style={styles.checkboxLabel}>{translate('usePatrolAddress')}</MyAppText>
                        </TouchableOpacity>

                        {!values.useParentAddress && (
                            <ActivitiesAndServicesLocationSelector
                                form={form}
                                lastCep={defaultValues.cep ?? ''}
                                setCep={(val) => form.setValue('cep', val)}
                                pin={pin}
                                setPin={setPin}
                                isEditing={!!initialValue && !initialValue.useParentAddress}
                            />
                        )}
                    </ScrollView>

                    <FormActions
                        viewStyle={{ flexDirection: 'row', justifyContent: 'space-around', width: '100%' }}
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9 }}
                        buttonContentContainerStyle={{ flex: 1 }}
                        cancelText="goBack"
                        confirmText="save"
                        onSubmit={handleSave}
                        onClose={onClose}
                        disabled={!form.formState.isValid}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    backdrop: {
        flex: 1,
        backgroundColor: '#31313199',
        justifyContent: 'flex-end',
    },
    sheet: (isMobile: boolean) => ({
        width: isMobile ? '100%' : 480,
        alignSelf: isMobile ? 'stretch' : 'center',
        maxHeight: '90%',
        backgroundColor: theme.colors.container.background,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        paddingHorizontal: 16,
        paddingTop: 12,
        paddingBottom: 16,
        gap: 16,
    }),
    sheetHandle: {
        alignSelf: 'center',
        width: 40,
        height: 4,
        borderRadius: 2,
        backgroundColor: theme.colors.card.border,
        marginBottom: 4,
    },
    modalHeader: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    smallFieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 13,
    },
    itemView: {
        gap: 8,
        flex: 1,
    },
    timePickerCol: {
        flexDirection: 'column',
        gap: 8,
        flex: 1,
    },
    timePickerLabelRow: {
        flexDirection: 'row',
        columnGap: 5,
    },
    requiredStar: {
        color: theme.colors.errors['300'],
    },
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10,
    },
    rangePreview: {
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 4,
    },
    rangePreviewText: {
        color: theme.colors.neutral['500'],
        fontSize: 14,
        fontWeight: '600',
    },
    rangePreviewDuration: {
        color: theme.colors.neutral['400'],
        fontSize: 12,
    },
    checkboxRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    checkbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkboxChecked: {
        borderColor: theme.colors.primary['500'],
        backgroundColor: theme.colors.primary['500'],
    },
    checkboxMark: {
        width: 9,
        height: 5,
        borderLeftWidth: 2,
        borderBottomWidth: 2,
        borderColor: theme.colors.white['500'],
        transform: [{ rotate: '-45deg' }],
        marginBottom: 2,
    },
    checkboxLabel: {
        fontSize: 14,
        color: theme.colors.neutral['500'],
    },
}));
